import classNames from 'classnames'
import React, { ComponentPropsWithRef, forwardRef, ReactElement } from 'react'
import { LinkProps } from 'react-router'
import { IconLoading } from '../icon-loading'
import styles from './index.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary'

interface Props {
  variant: ButtonVariant
  small?: boolean
  className?: string
}

interface PropsWithIsLoading extends Props {
  isLoading?: boolean
}

export type ButtonBaseProps = PropsWithIsLoading & ComponentPropsWithRef<'button'>

// A basic <button> component.
export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(function ButtonBase(props, ref): ReactElement {
  const enhancedProps = { ...props, disabled: props.disabled || props.isLoading }

  return (
    <button {...nativeElementProps(enhancedProps)} className={createButtonClassName(enhancedProps)} ref={ref}>
      {props.isLoading && <IconLoading />}
      {/* Browser translation replaces bare text nodes, and React then fails to insert the spinner before them */}
      <span className={styles.label}>{props.children}</span>
    </button>
  )
})

// A <Link> that looks like a button (for internal links).
export const ButtonBaseLink = forwardRef<
  HTMLAnchorElement,
  Props & {
    to: string
    as: React.ElementType<React.RefAttributes<HTMLAnchorElement> & LinkProps>
  } & ComponentPropsWithRef<'a'>
>(function ButtonBaseLink(props, ref): ReactElement {
  const Link = props.as
  return (
    <Link {...nativeElementProps(props)} className={createButtonClassName(props)} ref={ref}>
      {props.children}
    </Link>
  )
})

// An <a> that looks like a button (for external links).
export const ButtonBaseA = forwardRef<HTMLAnchorElement, Props & ComponentPropsWithRef<'a'>>(
  function ButtonBaseA(props, ref): ReactElement {
    return (
      <a {...nativeElementProps(props)} className={createButtonClassName(props)} ref={ref}>
        {props.children}
      </a>
    )
  }
)

// Create the className for a button based on the given props.
function createButtonClassName(props: Props): string {
  return classNames(styles.button, styles[props.variant], props.small && styles.small, props.className)
}

// Extract the props that should be forwarded to the native HTML element (<a> or <button>).
function nativeElementProps<T extends PropsWithIsLoading>(props: T) {
  const { isLoading, variant, small, className, ...rest } = props
  return rest
}
